import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { FileText, Plus, Eye, Edit } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useEffect, useState, useCallback } from 'react';
import { fetchRelatedDocuments } from '../utils/common';
import { db } from '@/js/external-js/config/main_config';
import { useTenancy } from '@/contexts/TenancyContext';
import { useMSGReadOnly } from '../contexts/ReadOnlyContext';
import { findFrameworkName } from '@/js/external-js/variableModules/utils/findFrameworkName.util';
import { toast } from 'sonner';
import { renderDate } from '@shared/renderDate';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import DocumentPage from '@/app/pages/document-detail';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface DocumentWithFrameworks {
    docID: string;
    fileName: string;
    displayName?: string;
    procRef?: string[];
    frameworkNames?: {
        name: string;
        clauses: string[]; // Track which clauses are associated with each framework
    }[];
}
interface SupportingDocsDropdownProps {
    pageRoute: string;
    clauseStandards?: { current: Set<string> } | Set<string>;
    pageClauses?: { current: Set<string> } | Set<string>;
    setSupportingDocs?: (docs: any[]) => void;
}

const SupportingDocsDropdown = ({
    pageRoute,
    clauseStandards = { current: new Set() },
    pageClauses = { current: new Set() },
    setSupportingDocs: setExternalSupportingDocs
}: SupportingDocsDropdownProps) => {
    const { activeTenancy } = useTenancy();
    const { isReadOnly } = useMSGReadOnly();
    const [supportingDocs, setSupportingDocs] = useState<DocumentWithFrameworks[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
    const [prefillData, setPrefillData] = useState<any>(null);
    const [dialogKey, setDialogKey] = useState(0);

    const getProcRefs = (clauses: any): string[] => {
        if (!clauses) return [];
        if (clauses.current) return Array.from(clauses.current);
        if (clauses instanceof Set || Array.isArray(clauses)) return Array.from(clauses);
        return [];
    };

    const handleAddDocument = () => {
        const procRefs = getProcRefs(pageClauses);
        const newPrefillData = {
            documentType: 'Policy',
            procRef: procRefs,
            procRefs: procRefs,
            relatedControls: procRefs,
            selectedControls: procRefs,
            msgPages: [pageRoute],
            status: 'Draft',
            title: `Supporting Document for ${pageRoute}`,
            description: `Document supporting the ${pageRoute} section`,
            dateAdded: renderDate(new Date())
        };

        setPrefillData(newPrefillData);
        setDialogKey(prev => prev + 1);
        setIsDocumentDialogOpen(true);
    };

    const DialogDocumentDetail = ({ documentID, prefillData }: { documentID?: string; prefillData?: any }) => {
        const [document, setDocument] = useState<any>(null);

        useEffect(() => {
            if (prefillData) {
                setDocument({
                    ...prefillData,
                    procRef: prefillData.procRef || [],
                    documentType: 'Policy',
                    status: prefillData.status || 'Draft',
                    dateAdded: prefillData.dateAdded || { __time__: new Date().toISOString() }
                });
            }
        }, [prefillData]);

        const handleSave = (savedDoc: any) => {
            toast.success('Document saved successfully');
            setIsDocumentDialogOpen(false);
            fetchDocuments();
        };

        if (!document) return null;

        return (
            <div className="flex flex-col h-full">
                <div className="flex-1 overflow-auto p-4">
                    <DocumentPage
                        {...(prefillData?.documentID ? { documentID: prefillData.documentID } : {})}
                        prefillData={prefillData}
                        onSave={handleSave}
                        onCancel={() => setIsDocumentDialogOpen(false)}
                        onBack={() => setIsDocumentDialogOpen(false)}
                        referer="Management System Guide"
                    />
                </div>
            </div>
        );
    };

    const updateFrameworkNames = useCallback(async (doc: any): Promise<DocumentWithFrameworks> => {
        if (!doc.procRef?.length) return { ...doc, frameworkNames: [] };

        try {
            const frameworkMap = new Map<string, Set<string>>();

            // Process each procRef to group by framework
            for (const ref of doc.procRef) {
                try {
                    const name = await findFrameworkName(ref);
                    if (name) {
                        if (!frameworkMap.has(name)) {
                            frameworkMap.set(name, new Set());
                        }
                        frameworkMap.get(name)?.add(ref); // Add the clause reference
                    }
                } catch (error) {
                    console.error(`Error finding framework name for ref ${ref}:`, error);
                }
            }

            // Convert to array of {name, clauses}
            const frameworkNames = Array.from(frameworkMap.entries()).map(([name, clauses]) => ({
                name,
                clauses: Array.from(clauses)
            }));

            return {
                ...doc,
                frameworkNames
            };
        } catch (error) {
            console.error('Error updating framework names:', error);
            return { ...doc, frameworkNames: [] };
        }
    }, []);
    const fetchDocuments = useCallback(async () => {
        if (!activeTenancy?.id || ["", "Loading..."].includes(activeTenancy.id)) return;

        setLoading(true);
        const currentClauses = getProcRefs(pageClauses);

        const unsubscribe = fetchRelatedDocuments(
            db,
            activeTenancy.id,
            pageRoute,
            async (matchingDocs) => {
                if (!matchingDocs || !Array.isArray(matchingDocs)) {
                    setSupportingDocs([]);
                    setLoading(false);
                    return;
                }

                try {
                    const processedDocs = await Promise.all(
                        matchingDocs.map(doc => updateFrameworkNames(doc))
                    );
                    const validDocs = processedDocs.filter(doc => doc && doc.docID);
                    setSupportingDocs(validDocs);
                    if (setExternalSupportingDocs) {
                        setExternalSupportingDocs(validDocs);
                    }
                } catch (error) {
                    console.error('Error processing documents:', error);
                    setSupportingDocs([]);
                } finally {
                    setLoading(false);
                }
            },
            currentClauses
        );

        return () => {
            if (unsubscribe && typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [activeTenancy?.id, pageRoute, updateFrameworkNames, pageClauses, setExternalSupportingDocs]);

    useEffect(() => {
        const cleanup = fetchDocuments();
        return () => {
            if (cleanup && typeof cleanup === 'function') {
                cleanup();
            }
        };
    }, [fetchDocuments]);

    if (loading) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="inline-flex"> {/* Added wrapper span */}
                            <Button
                                variant="ghost"
                                size="icon"
                                disabled
                                className="relative"
                            >
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="h-4 w-4 rounded-full bg-primary/20 animate-ping" />
                                </div>
                                <FileText className="h-4 w-4 relative z-10 text-muted-foreground" />
                                <span className="sr-only">Loading supporting documents</span>
                            </Button>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-center bg-card/40 shadow-sm backdrop-blur-md text-primary dark:bg-card/40 dark:text-primary">
                        <p>Loading supporting documents...</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            This may take a moment as we gather all relevant documents.
                        </p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }
    return (
        <>
            <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="backdrop-blur-md bg-white/80 hover:bg-white dark:bg-secondary/90 dark:hover:bg-card/90 transition-all duration-200 ease-in-out shadow-sm hover:shadow-lg hover:shadow-primary/30 hover:scale-105 text-gray-800 dark:text-gray-100"
                    >
                        <FileText className="h-4 w-4" />
                        <span className="sr-only">Supporting Documents</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    className="w-80 p-0 bg-white/95 dark:bg-secondary/95 backdrop-blur-lg border border-gray-200 dark:border-gray-700/80 shadow-2xl shadow-black/10 dark:shadow-black/30 rounded-xl overflow-hidden"
                >
                    <div className="px-4 py-3 text-sm font-semibold text-gray-800 dark:text-gray-100 bg-white/90 dark:bg-secondary/90 border-b border-gray-100 dark:border-gray-700/80 flex justify-between items-center">
                        <span>Supporting Documents</span>
                        {!isReadOnly && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs px-2"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddDocument();
                                }}
                            >
                                <Plus className="h-3.5 w-3.5 mr-1" />
                                Add
                            </Button>
                        )}
                    </div>
                    <ScrollArea className="h-60">
                        <div className="p-2">
                            {supportingDocs.length === 0 ? (
                                <div className="px-3 py-8 text-center">
                                    <FileText className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                                    <p className="text-sm text-gray-500 dark:text-gray-400">No supporting documents found</p>
                                    {!isReadOnly && (
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Click 'Add' to create one</p>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    {supportingDocs.map((doc) => (
                                        <div
                                            key={doc.docID}
                                            className="group relative p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                                        >
                                            <div
                                                className="font-medium text-sm text-gray-800 dark:text-gray-200 cursor-pointer hover:underline"
                                                onClick={() => window.open(`/documentdetail/${doc.docID}`, '_blank')}
                                            >
                                                {doc.displayName || doc.fileName || 'Untitled Document'}
                                            </div>
                                            {!isReadOnly && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPrefillData({
                                                            documentID: doc.docID,
                                                            title: doc.displayName || doc.fileName,
                                                            isEdit: true
                                                        });
                                                        setDialogKey(prev => prev + 1);
                                                        setIsDocumentDialogOpen(true);
                                                    }}
                                                    className="absolute right-2 top-2 p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-accent transition-opacity"
                                                    title="Edit document"
                                                >
                                                    <Edit className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                            {doc.frameworkNames?.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-1.5">
                                                    <TooltipProvider>
                                                        {doc.frameworkNames.slice(0, 3).map(({ name, clauses }, i) => (
                                                            <Tooltip key={i}>
                                                                <TooltipTrigger asChild>
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/20 text-primary dark:bg-primary/20 dark:text-primary cursor-help">
                                                                        {name}
                                                                    </span>
                                                                </TooltipTrigger>
                                                                <TooltipContent className="bg-card/40 shadow-sm backdrop-blur-md text-primary dark:bg-card/40 dark:text-primary">
                                                                    <div className="max-w-xs">
                                                                        <p className="font-medium mb-1">Used by clauses:</p>
                                                                        <ul className="space-y-1">
                                                                            {clauses.map((clause, idx) => (
                                                                                <li key={idx} className="text-xs text-foreground dark:text-foreground">
                                                                                    - {clause}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        ))}
                                                        {doc.frameworkNames.length > 3 && (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300 cursor-help">
                                                                        +{doc.frameworkNames.length - 3} more
                                                                    </span>
                                                                </TooltipTrigger>
                                                                <TooltipContent className="bg-card/40 shadow-sm backdrop-blur-md text-primary dark:bg-card/40 dark:text-primary">
                                                                    <div className="max-w-xs">
                                                                        <p className="font-medium mb-1">Additional frameworks:</p>
                                                                        <div className="space-y-1">
                                                                            {doc.frameworkNames.slice(3).map(({ name, clauses }, i) => (
                                                                                <div key={i} className="flex flex-col">
                                                                                    <span className="font-medium">{name}</span>
                                                                                    <ul className="ml-2">
                                                                                        {clauses.map((clause, idx) => (
                                                                                            <li key={idx} className="text-xs text-muted-foreground">
                                                                                                - {clause}
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                    </TooltipProvider>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                    {supportingDocs.length > 0 && (
                        <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 text-right border-t border-gray-100 dark:border-gray-700/80 bg-white/80 dark:bg-secondary/80">
                            {supportingDocs.length} document{supportingDocs.length !== 1 ? 's' : ''} available
                        </div>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={isDocumentDialogOpen} onOpenChange={setIsDocumentDialogOpen}>
                <DialogContent
                    className="w-screen h-screen max-w-none p-0 m-0 rounded-none bg-card/40"
                    style={{
                        width: '100vw',
                        maxWidth: 'none',
                        margin: 0,
                        padding: 0,
                    }}
                >
                    <div className="h-full">
                        <DialogDocumentDetail
                            key={dialogKey}
                            prefillData={prefillData}
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default SupportingDocsDropdown;